/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { removeListItem } from "../util";
import { AbstractControl, isOptionsObj, pickAsyncValidators, pickValidators } from "./abstract_model";
/**
 * FormControlState is a boxed form value. It is an object with a `value` key and a `disabled` key.
 *
 * @publicApi
 */

function isFormControlState(formState) {
  return typeof formState === "object" && formState !== null && Object.keys(formState).length === 2 && "value" in formState && "disabled" in formState;
}

export const FormControl = class FormControl extends AbstractControl {
  /** @publicApi */

  /** @internal */

  /** @internal */

  /** @internal */
  constructor( // formState and defaultValue will only be null if T is nullable
  formState = null, validatorOrOpts, asyncValidator) {
    super(pickValidators(validatorOrOpts), pickAsyncValidators(asyncValidator, validatorOrOpts));
    this.defaultValue = null;
    this._onChange = [];
    this._pendingValue = void 0;
    this._pendingChange = false;

    this.onChange = event => {
      const value = getControlValue(event);
      const isDirty = value !== this.value;

      if (this.updateOn !== "change") {
        this._pendingValue = value;
        this._pendingChange = true;

        if (isDirty && !this._pendingDirty) {
          this._pendingDirty = true;
        }
      } else {
        if (isDirty && !this.dirty) {
          this.markAsDirty();
        }

        this.setValue(value);
      }
    };

    this.onBlur = () => {
      if (this.updateOn === "blur") {
        if (this._pendingDirty && !this.dirty) {
          this.markAsDirty();
        }

        if (!this.touched) {
          this.markAsTouched();
        }

        this.root.forceUpdate();
      } else if (this.updateOn === "submit") {
        this._pendingTouched = true;
      } else {
        const emitChangeToView = !this.touched;

        if (!this.touched) {
          this.markAsTouched();
          this.root.forceUpdate();
        }

        if (emitChangeToView) {}
      }
    };

    this._applyFormState(formState);

    this._setUpdateStrategy(validatorOrOpts);

    this._initObservables();

    this.updateValueAndValidity({
      onlySelf: true,
      // If `asyncValidator` is present, it will trigger control status change from `PENDING` to
      // `VALID` or `INVALID`.
      // The status should be broadcasted via the `statusChanges` observable, so we set
      // `emitEvent` to `true` to allow that during the control creation process.
      emitEvent: !!this.asyncValidator
    });

    if (isOptionsObj(validatorOrOpts) && (validatorOrOpts.nonNullable || validatorOrOpts.initialValueIsDefault)) {
      if (isFormControlState(formState)) {
        this.defaultValue = formState.value;
      } else {
        this.defaultValue = formState;
      }
    }
  }

  setValue(value, options = {}) {
    this.value = this._pendingValue = value;
    /****** WDS:Modified ******/

    const forceUpdate = this.root.forceUpdate;
    forceUpdate && forceUpdate();
    /**************************/

    if (this._onChange.length && options.emitModelToViewChange !== false) {
      this._onChange.forEach(changeFn => changeFn(this.value, options.emitViewToModelChange !== false));
    }

    this.updateValueAndValidity(options);
  }

  patchValue(value, options = {}) {
    this.setValue(value, options);
  }

  reset(formState = this.defaultValue, options = {}) {
    this._applyFormState(formState);

    this.markAsPristine(options);
    this.markAsUntouched(options);
    this.setValue(this.value, options);
    this._pendingChange = false;
  }
  /**  @internal */


  _updateValue() {}
  /**  @internal */


  _anyControls(condition) {
    return false;
  }
  /**  @internal */


  _allControlsDisabled() {
    return this.disabled;
  }

  registerOnChange(fn) {
    this._onChange.push(fn);
  }
  /** @internal */


  _unregisterOnChange(fn) {
    removeListItem(this._onChange, fn);
  }

  registerOnDisabledChange(fn) {
    this._onDisabledChange.push(fn);
  }
  /** @internal */


  _unregisterOnDisabledChange(fn) {
    removeListItem(this._onDisabledChange, fn);
  }
  /** @internal */


  _forEachChild(cb) {}
  /** @internal */


  _syncPendingControls() {
    if (this.updateOn === "submit") {
      if (this._pendingDirty) this.markAsDirty();
      if (this._pendingTouched) this.markAsTouched();

      if (this._pendingChange) {
        this.setValue(this._pendingValue, {
          onlySelf: true,
          emitModelToViewChange: false
        });
        return true;
      }
    }

    return false;
  }

  _applyFormState(formState) {
    if (isFormControlState(formState)) {
      this.value = this._pendingValue = formState.value;
      formState.disabled ? this.disable({
        onlySelf: true,
        emitEvent: false
      }) : this.enable({
        onlySelf: true,
        emitEvent: false
      });
    } else {
      this.value = this._pendingValue = formState;
    }
  }
  /****** WDS:Modified ******/

  /***** Based on https://www.npmjs.com/package/react-reactive-form ******/

  /**************************/


};
/****** WDS:Modified ******/

function getControlValue(event) {
  if (isEvent(event)) {
    switch (event.target.type) {
      case "checkbox":
        return event.target.checked;

      case "select-multiple":
        if (event.target.options) {
          let options = event.target.options;
          var value = [];

          for (var i = 0, l = options.length; i < l; i++) {
            if (options[i].selected) {
              value.push(options[i].value);
            }
          }

          return value;
        }

        return event.target.value;

      case "number":
        return event.target.valueAsNumber;

      default:
        return isReactNative() ? event.nativeEvent.text : event.target.value;
    }
  }

  return event;
}

export const isReactNative = () => typeof window !== "undefined" && window.navigator && window.navigator.product && window.navigator.product === "ReactNative";
export const isEvent = candidate => !!(candidate && candidate.stopPropagation && candidate.preventDefault);
/**************************/

export const UntypedFormControl = FormControl;
/**
 * @description
 * Asserts that the given control is an instance of `FormControl`
 *
 * @publicApi
 */

export const isFormControl = control => control instanceof FormControl;